import React, { useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { Link, Redirect, useHistory, useParams } from 'react-router-dom';
import { FaCheck } from 'react-icons/fa';
import {
    Box,
    Container,
    Flex,
    Heading,
    Link as ChakraLink,
    List,
    ListItem,
    ListIcon,
    Spacer,
    Stack,
    Text,
    Checkbox,
    useBreakpointValue, Alert, AlertIcon,
} from '@chakra-ui/react';
import { useForm, FormProvider, useFormContext, useWatch } from 'react-hook-form';
import { coreUrls } from "core_src/js/core_urls_config";
import { userStatus } from "core_src/js/user_config";
import { analytics, anvilAnalyticsConfig } from "anvil-analytics/analytics";
import { AnvilInput, AnvilSelect } from "frontend/form/components";
import { useAnvilProps } from "frontend/hooks";
import { BraintreeDropIn } from 'frontend/order_tunnel/components';
import axios from "axios";
import { generatePricePeriodText } from "frontend/order_tunnel/utils/braintree";
import { RichText } from "frontend/wagtail/components/RichText";
import { debounce } from "throttle-debounce";
import { ColorButton } from "frontend/core/components/ColorButton";

/**
 * Component for displaying the selected subscription information.
 *
 * Valid props:
 * - name: The name of the subscription.
 * - oldPrice: The previous price of the subscription.
 * - price: The current price of the subscription.
 * - pricePeriodText: Text displayed underneath the price explaining the duration of the price.
 * - features: List of features for the subscription.
 * - billingDescription: The billing breakdown explanation text for the subscription.
 * - showFullCard: Whether or not to show all the information or just the header.
 */
function SelectedPlanCard(props) {

    const {
        name,
        oldPrice,
        price,
        pricePeriodText,
        features,
        billingDescription,
        showFullCard,
        ...wrapperProps
    } = props;

    return (
        <Box layerStyle="card" h="fit-content" maxW={["sm", null, "2xs"]} {...wrapperProps}>
            <Text fontSize="xs">Selected subscription</Text>
            <Heading as="h3" size="md" mt={1}>{name}</Heading>
            <ChakraLink as={Link} to="/plans" fontSize="xs">(change)</ChakraLink>
            <Box w="fit-content">
                <Stack mt={4} spacing={0}>
                    {oldPrice &&
                    <Heading as="h5" size="md" fontWeight="normal" textDecoration="line-through" color="gray" mb={0} lineHeight={"inherit"}>
                        ${oldPrice}
                    </Heading>
                    }
                    <Heading as="h4" size="lg" fontWeight="bold" mt={0}>
                        {price}
                    </Heading>
                </Stack>
                <Text fontSize="sm">{pricePeriodText}</Text>
            </Box>
            {features &&
            <List spacing={2} mt={6}>
                {features.map((feature, index) => (
                    <ListItem key={index} display="flex" lineHeight={6} fontSize={"sm"}>
                        <ListIcon as={FaCheck} h={6}/>
                        {feature}
                    </ListItem>
                ))}
            </List>
            }
            {showFullCard &&
            <Text fontSize="xs" lineHeight={1} mt={6}>
                {billingDescription}
            </Text>
            }
        </Box>
    );
}


/**
 * Show a message letting users know that they are already logged in.
 *
 * Valid props:
 *  - email: The email address for the currently logged in user.
 */
function LoggedInMessageSection(props) {

    const {
        email,
    } = props;

    return (
        <Stack spacing="4">
            <Text>
                You are logged in as: <Text as="b">{email}</Text>
                {' '}
            </Text>
        </Stack>
    );
}


/**
 * Ask the user to enter their email address.
 */
function EmailFormSection() {
    return (
        <Stack spacing="4">
            <Text lineHeight={1}>
                You&apos;ll use this email to sign in. If this is a new account, we&apos;ll ask you to set a password later.
            </Text>
            <AnvilInput name="email" labelText="Email address" isRequired={true} inputProps={{
                type: 'email',
            }}/>
        </Stack>
    );
}

/**
 * Ask the user to enter their phone number.
 */
function PhoneFormSection() {
    return (
        <Stack spacing="4">
            <Text lineHeight={1}>
                The plan you selected requires a phone number.
            </Text>
            <AnvilInput name="phone" labelText="Phone number" isRequired={true} inputProps={{
                type: 'tel',
            }}/>
        </Stack>
    );
}

/**
 * Ask the user for their delivery information.
 *
 * Valid props:
 *  - formNamespace: The namespace for the form html.
 *  - isDisabled: whether the form is disabled.
 */
function AddressFormSection(props) {
    const {
        formNamespace,
        isDisabled,
    } = props;

    const {settings} = useAnvilProps();

    const getName = useCallback((name) => {
        if (!formNamespace) return name;

        return `${formNamespace}.${name}`;
    }, [formNamespace]);

    const fieldDirection = useBreakpointValue({
        base: "column",
        md: "row"
    });

    return (
        <Stack spacing="4">
            <Stack spacing="4" direction={fieldDirection}>
                <AnvilInput labelText="First name"
                            isRequired={true}
                            name={getName('first_name')}
                            isDisabled={isDisabled}
                />
                <AnvilInput labelText="Last name"
                            isRequired={true}
                            name={getName('last_name')}
                            isDisabled={isDisabled}
                />
            </Stack>
            <AnvilInput labelText="Address line 1"
                        isRequired={true}
                        name={getName('street_address')}
                        isDisabled={isDisabled}
            />
            <AnvilInput labelText="Address line 2"
                        name={getName('extended_address')}
                        isDisabled={isDisabled}
            />
            <Stack direction={fieldDirection} spacing="4">
                <Stack direction={"row"} spacing={"4"} flexBasis={"70%"}>

                    <AnvilInput labelText="City"
                                isRequired={true}
                                name={getName('locality')}
                                isDisabled={isDisabled}
                    />
                    <AnvilSelect name={getName('region')}
                                 labelText="State"
                                 isRequired={true}
                                 maxW="20"
                                 isDisabled={isDisabled}
                                 defaultValue={settings.default_state}
                    >
                        <option value="AL">AL</option>
                        <option value="AK">AK</option>
                        <option value="AS">AS</option>
                        <option value="AZ">AZ</option>
                        <option value="AR">AR</option>
                        <option value="AA">AA</option>
                        <option value="AE">AE</option>
                        <option value="AP">AP</option>
                        <option value="CA">CA</option>
                        <option value="CO">CO</option>
                        <option value="CT">CT</option>
                        <option value="DE">DE</option>
                        <option value="DC">DC</option>
                        <option value="FL">FL</option>
                        <option value="GA">GA</option>
                        <option value="GU">GU</option>
                        <option value="HI">HI</option>
                        <option value="ID">ID</option>
                        <option value="IL">IL</option>
                        <option value="IN">IN</option>
                        <option value="IA">IA</option>
                        <option value="KS">KS</option>
                        <option value="KY">KY</option>
                        <option value="LA">LA</option>
                        <option value="ME">ME</option>
                        <option value="MD">MD</option>
                        <option value="MA">MA</option>
                        <option value="MI">MI</option>
                        <option value="MN">MN</option>
                        <option value="MS">MS</option>
                        <option value="MO">MO</option>
                        <option value="MT">MT</option>
                        <option value="NE">NE</option>
                        <option value="NV">NV</option>
                        <option value="NH">NH</option>
                        <option value="NJ">NJ</option>
                        <option value="NM">NM</option>
                        <option value="NY">NY</option>
                        <option value="NC">NC</option>
                        <option value="ND">ND</option>
                        <option value="MP">MP</option>
                        <option value="OH">OH</option>
                        <option value="OK">OK</option>
                        <option value="OR">OR</option>
                        <option value="PA">PA</option>
                        <option value="PR">PR</option>
                        <option value="RI">RI</option>
                        <option value="SC">SC</option>
                        <option value="SD">SD</option>
                        <option value="TN">TN</option>
                        <option value="TX">TX</option>
                        <option value="UT">UT</option>
                        <option value="VT">VT</option>
                        <option value="VI">VI</option>
                        <option value="VA">VA</option>
                        <option value="WA">WA</option>
                        <option value="WV">WV</option>
                        <option value="WI">WI</option>
                        <option value="WY">WY</option>
                    </AnvilSelect>
                </Stack>
                <AnvilInput labelText="Postal code"
                            name={getName('postal_code')}
                            isRequired={true}
                            flexBasis={"33%"}
                            isDisabled={isDisabled}
                />
            </Stack>
        </Stack>
    );
}


/**
 * Wraps AddressFormSection with logic to copy values from billing_address to delivery_address.
 *
 * Valid props:
 *  - handleUseBillingAddress: Callback to lift up state.
 *  - hasBilling: Whether the BillingScreen has a billing address form.
 */
function DeliveryAddressFormSection(props) {
    const {
        handleUseBillingAddress,
        hasBilling,
    } = props;

    const {
        control,
        setValue
    } = useFormContext();

    const [useBillingAddress, setUseBillingAddress] = useState(false)

    const field_names = useMemo(() => [
        'first_name',
        'last_name',
        'street_address',
        'extended_address',
        'locality',
        'region',
        'postal_code'
    ], [])

    const delivery_address_field_names = useMemo(() => {
        return field_names.map((item) => `delivery_address.${item}`)
    }, [field_names])

    const billing_address_field_names = useMemo(() => {
        return field_names.map((item) => `billing_address.${item}`)
    }, [field_names])

    const values = useWatch({
        control,
        name: billing_address_field_names,
        defaultValue: null
    })

    const updateDeliveryFields = useMemo(() =>
        debounce(100, false, (useBillingAddress, values) => {
            if (useBillingAddress && (values !== null)) {
                delivery_address_field_names.map((name, index) => {
                    setValue(name, values[index]);
                });
            }
        }), [delivery_address_field_names, setValue]);

    useEffect(() => {
        updateDeliveryFields(useBillingAddress, values)
    }, [useBillingAddress, values, updateDeliveryFields])

    // Create the Checkbox here, and add it into the AddressFormSection.
    const SameAsBillingCheckbox = useCallback(() => {
        if (!hasBilling) {
            return null;
        }

        return (
            <Checkbox
                isChecked={useBillingAddress}
                onChange={(e) => {
                    setUseBillingAddress(e.target.checked)
                    handleUseBillingAddress(e.target.checked)
                }}
            >Same as Billing Address</Checkbox>)
    }, [hasBilling, useBillingAddress, handleUseBillingAddress])

    return (<>
        <SameAsBillingCheckbox/>
        <AddressFormSection
            isDisabled={useBillingAddress}
            {...props}
        />
    </>
    )
}


/**
 * Handles logged in/anonymous users completing the purchase of a print or digital subscription.
 *
 * Valid props:
 *  - braintreeClientToken: The braintree client token to initialize the drop-in fields with. Only used for storybook.
 *  - selectedPlan: The rate card that the user selected in the Plan screen.
 */
export function BillingScreenContents(props) {
    const {
        // Currently only used for storybook/storyshots
        braintreeClientToken,
        selectedPlan
    } = props;

    useEffect(() => {
        analytics.page(anvilAnalyticsConfig.page_payload);
    }, [])

    let history = useHistory();

    const {product_pk} = useParams();
    const {
        messages,
        settings,
        metadata
    } = useAnvilProps();


    const formSectionRef = useRef();

    const formOptions = useForm();

    let userEmail = null;
    if (userStatus.authenticated) {
        userEmail = userStatus.email;
    }

    const {
        handleSubmit,
    } = formOptions;

    const [submissionError, setSubmissionError] = useState(null);
    const [isSubmittingForm, setIsSubmittingForm] = useState(false);
    const [braintreeError, setBraintreeError] = useState('');

    const [braintreeInstance, setBraintreeInstance] = useState();
    const [braintreeCustomerId, setBraintreeCustomerId] = useState();

    // Braintree drop in fields have been initialized.
    const onInitialization = useCallback(async ({dropInInstance, customerId}) => {
        setBraintreeInstance(dropInInstance);
        setBraintreeCustomerId(customerId);
    }, []);

    // A valid payment method has been entered
    const onPaymentMethodRequestable = useCallback(() => {
        // Clear any old errors
        setBraintreeError(null);
    }, []);

    // Only show the full card for medium displays and larger
    const showFullCard = useBreakpointValue({
        base: false,
        md: true
    });

    const isPrint = useMemo(() => selectedPlan.is_print, [selectedPlan]);
    const requiresPhone = useMemo(() => selectedPlan.requires_phone && !userStatus.has_phone, [selectedPlan]);

    const [useBillingAddressForDelivery, setUseBillingAddressForDelivery] = useState(false)

    // Helper function for displaying an error message above the braintree fields and scrolling it into view.
    const showBraintreeError = useCallback((errorMessage) => {
        // Attempt to scroll back up to the top of the form
        if (formSectionRef && formSectionRef.current) {
            formSectionRef.current.scrollIntoView(true);
        }
        setBraintreeError(errorMessage);
        setIsSubmittingForm(false);
    }, []);

    const handleFormSubmit = useCallback(async (data) => {
        // Clear any previous errors
        setSubmissionError('');
        // Show the loading indicator
        setIsSubmittingForm(true);

        // If we can't request the payment method, then show an error message
        if (!braintreeInstance.isPaymentMethodRequestable()) {
            showBraintreeError("Please enter your payment information.");
            return;
        }

        // Get the braintree nonce
        let braintreePaymentNonce;
        let braintreePaymentType;
        let braintreePaymentIsVaulted;
        let braintreeFullResponse;
        try {
            const response = await braintreeInstance.requestPaymentMethod();

            const {
                nonce,
                type,
                vaulted = false,
            } = response;

            braintreeFullResponse = response;
            braintreePaymentNonce = nonce;
            braintreePaymentType = type;
            braintreePaymentIsVaulted = vaulted;
        } catch (e) {
            showBraintreeError("There was an error verifying your payment information.");
            return;
        }

        if (useBillingAddressForDelivery) {
            data['delivery_address'] = data['billing_address'];
        }

        // "product_pk" just refers to the product card selected and routed in the URL.
        // When circulation is enabled, this will be a CirculationRateCard.
        // In order to handle differently, send a different key to the API depending on whether this is circulation.
        const productOrRateCard = selectedPlan.braintree ? {"product": product_pk} : {"circulation_rate_card": product_pk}


        // The payload containing the purchase information anvil needs to create the subscription
        const orderTunnelPayload = {
            'customer_id': braintreeCustomerId,
            'email': userEmail,
            'client_nonce': braintreePaymentNonce,
            'vaulted': braintreePaymentIsVaulted,
            'order_tunnel_page': metadata.page.id,
            'braintree_drop_in_response': braintreeFullResponse,
            ...productOrRateCard,
            ...data,
        };

        analytics.track('AddPaymentInfo', {
            products: [{
                'id': selectedPlan.braintree ? selectedPlan.braintree.id : selectedPlan.rate_card_pk,
                'name': selectedPlan.braintree ? selectedPlan.braintree.name : selectedPlan.rate_card_title,
                'price': selectedPlan.braintree ? selectedPlan.braintree.upfront_cost : selectedPlan.upfront_cost,
                'currency': selectedPlan.braintree ? selectedPlan.braintree.currency_iso_code : 'USD',
                'category': selectedPlan.is_print ? 'Print & Digital' : 'Digital',
                'trial': selectedPlan.braintree ? !!(selectedPlan.braintree.trial_period) : selectedPlan.trial,
                'discount': selectedPlan.braintree ? !!(selectedPlan.braintree.discounts) : selectedPlan.discount,
                'quantity': 1,
                'type': 'Subscription'
            }],
            total: selectedPlan.braintree ? selectedPlan.braintree.upfront_cost : selectedPlan.upfront_cost,
            payment_method: (braintreePaymentType === 'PayPalAccount' ? 'Paypal' : 'Direct'),
            store: 'Braintree',
            step: 2
            }
        );

        let response;
        // Send the data to anvil
        try {
            response = await axios.post(coreUrls.order_tunnel_api, orderTunnelPayload);
        } catch (e) {
            console.error('Error submitting purchase', e);
            // Clear loader
            setIsSubmittingForm(false);

            if (e.response && e.response.status === 400 && e.response.data) {
                const responseData = e.response.data;
                if (Array.isArray(responseData) && responseData[0] === "User already has a subscription.") {
                    // Handle unauthenticated user already has a subscription error.
                    setSubmissionError(
                        <Text>
                            The account you are trying to purchase a subscription for already has an active
                            subscription.
                            You can <ChakraLink href={coreUrls.login}>log in to your account, </ChakraLink>
                            or enter a different email and try again.
                        </Text>
                    );
                }

                // Handle specific error for missing names in print subscription
                else if (Array.isArray(responseData) && responseData[0] === "First and last name must be provided " +
                    "for a print subscription.") {
                    setSubmissionError(
                        <Text>
                            Please provide both first and last names for a print subscription. Make sure you have
                            filled in all required fields.
                        </Text>
                     );
                 }
                // Handle and non field errors that may have occurred
                else if (responseData.non_field_errors) {
                    const nonFieldErrors = responseData.non_field_errors;

                    if (nonFieldErrors[0] === "Print delivery is not available in your area.") {
                        setSubmissionError(
                            <Text>
                                This subscription is not available for delivery in your
                                area.
                                {' '}
                                <Link to="/plans">
                                    Check out our other subscriptions.
                                </Link>
                            </Text>
                        )
                    }
                }
            } else {
                // Show a generic submission error above the submit button
                setSubmissionError(<Text>Unable to complete purchase. Please try again.</Text>);
            }
            return;
        }

        const {subscription} = response.data;

        analytics.track('Purchase', {
            products: [{
                'id': selectedPlan.braintree ? selectedPlan.braintree.id : selectedPlan.rate_card_pk,
                'name': selectedPlan.braintree ? selectedPlan.braintree.name : selectedPlan.rate_card_title,
                'price': selectedPlan.braintree ? selectedPlan.braintree.upfront_cost : selectedPlan.upfront_cost,
                'currency': selectedPlan.braintree ? selectedPlan.braintree.currency_iso_code : 'USD',
                'category': selectedPlan.is_print ? 'Print & Digital' : 'Digital',
                'trial': selectedPlan.braintree ? !!(selectedPlan.braintree.trial_period) : selectedPlan.trial,
                'discount': selectedPlan.braintree ? !!(selectedPlan.braintree.discounts) : selectedPlan.discount,
                'quantity': 1,
                'type': 'Subscription'
            }],
            total: selectedPlan.braintree ? selectedPlan.braintree.upfront_cost : selectedPlan.upfront_cost,
            payment_method: (braintreePaymentType === 'PayPalAccount' ? 'Paypal' : 'Direct'),
            store: 'Braintree',
            order_id: subscription.transaction_id,
            step: 3
        });


        setIsSubmittingForm(false);

        // Send the user to the next step
        if (userStatus.authenticated) {
            // User already logged in so we send them directly to the thanks page
            history.push('/thanks');
        } else {
            let firstName = "";
            let lastName = "";
            if (data['delivery_address']) {
                firstName = data['delivery_address']['first_name']
                lastName = data['delivery_address']['last_name']
            }
            const {account} = response.data;
            const {
                email,
                has_existing_account: hasExistingAccount,
                has_usable_password: hasUsablePassword,
                social_providers: socialProviders
            } = account;
            // User still needs to authenticate the account the subscription was created for
            history.push({
                pathname: '/auth',
                state: {
                    email,
                    firstName,
                    lastName,
                    hasExistingAccount,
                    hasUsablePassword,
                    socialProviders,
                }
            });
        }
    }, [
        braintreeInstance,
        useBillingAddressForDelivery,
        userEmail,
        product_pk,
        metadata.page.id,
        selectedPlan.braintree,
        selectedPlan.is_print,
        showBraintreeError,
        history,
        braintreeCustomerId
    ]);

    // User already has a paid subscription so redirect them away
    if (userStatus.has_paid_subscription && !settings.is_admin_preview) {
        return <Redirect to={'/already-subbed'}/>
    }

    return (
        <FormProvider {...formOptions}>
            <form onSubmit={handleSubmit(handleFormSubmit)}>
                <Container maxW="4xl">
                    <RichText mb={[4, 8]} maxW={"lg"} data={messages.billing_body}/>
                    <Flex direction={["column", null, "row"]}>
                        <Stack spacing={4} order={[2, null, 0]} maxW="md">
                            <Stack spacing={4} ref={formSectionRef}>
                                <Heading size={"md"}>{messages.billing_payment_information_header}</Heading>
                                {!!braintreeError && (
                                    <Alert status="error" variant="subtle">
                                        <AlertIcon/>
                                        {braintreeError}
                                    </Alert>
                                )}
                                <BraintreeDropIn clientToken={braintreeClientToken}
                                                 onInitialization={onInitialization}
                                                 onPaymentMethodRequestable={onPaymentMethodRequestable}
                                                 paypalEnabled={settings.paypal_enabled}
                                />

                                {!!settings.billing_collect_billing_address && (
                                    <>
                                        <Heading size="md">Billing address</Heading>
                                        <AddressFormSection formNamespace="billing_address"/>
                                    </>
                                )}
                                {isPrint && (
                                    <>
                                        <Heading size="md">Delivery address</Heading>
                                        <DeliveryAddressFormSection formNamespace="delivery_address"
                                                                    hasBilling={!!settings.billing_collect_billing_address}
                                                                    handleUseBillingAddress={(useBillingAddress) => {
                                                                        setUseBillingAddressForDelivery(useBillingAddress);
                                                                    }}/>
                                    </>
                                    )
                                }

                                <Heading size={"md"}>{messages.billing_account_information_header}</Heading>
                                {userEmail && <LoggedInMessageSection email={userEmail}/>}
                                {!userEmail && <EmailFormSection/>}
                                {requiresPhone && <PhoneFormSection/>}
                            </Stack>
                            {!!submissionError && (
                                <Alert status="error" variant="subtle">
                                    <AlertIcon/>
                                    {submissionError}
                                </Alert>
                            )}
                            <Box w="full">
                                <ColorButton type="submit"
                                        isFullWidth={true}
                                        isLoading={isSubmittingForm}
                                        isDisabled={isSubmittingForm}
                                        buttonColor={messages.billing_submit_button_color}
                                >
                                    {messages.billing_submit_button_text}
                                </ColorButton>
                                {!showFullCard && (
                                    <Text fontSize="xs" lineHeight={1} mt={2}>{selectedPlan.fine_print}</Text>
                                )}
                            </Box>
                        </Stack>
                        <Spacer order={[1, null, 1]} minH={4}/>
                        <SelectedPlanCard name={selectedPlan.title}
                                          oldPrice={selectedPlan.original_price}
                                          // TODO: Price period text for Circulation.
                                          pricePeriodText={selectedPlan.price_period_override || (selectedPlan.braintree && generatePricePeriodText(selectedPlan))}
                                          price={selectedPlan.price}
                                          features={selectedPlan.features}
                                          billingDescription={selectedPlan.fine_print}
                                          showFullCard={showFullCard}
                                          order={[0, null, 2]}/>
                    </Flex>
                </Container>
            </form>
        </FormProvider>
    );
}

/**
 * Wrapper for the BillingScreenContents to validate that the plan exists.
 * Necessary to maintain consistency with react hooks.
 */
export default function BillingScreen(props){
    const {product_listings} = useAnvilProps();
    const {product_pk} = useParams();
    const selectedPlan = product_listings.find(product => (product.product_pk || product.rate_card_pk) === parseInt(product_pk));

    // If user is directly linked to a plan that no longer exists, send them back to plan page
    if (!selectedPlan) {
        return <Redirect to={"/plans"}/>
    }

    return <BillingScreenContents selectedPlan={selectedPlan} {...props}/>
}
